/**
 * MEGA CHIBRE — flesh-sculpture realism pass (2026-11-04, owner: "improve each
 * map", "realistic", "get inspired by existing models, don't copy them").
 *
 * The map is the owner's adult-humour floating anatomical sculpture: shaft
 * platform, glans with a corona ridge, twin mounds on a scrotum slab, vein
 * ridges as cover. Before this pass every piece was a flat pink plaster box.
 * Now each collider wears a soft FLESH SHELL (fleshShell.ts): rounded edges,
 * pillowy swelling toward the middle of every face, skin-tone vertex colour
 * with blotchy redness and contact darkening, and one of three procedural
 * skin families (fleshTextures.ts) — pored skin with rhombic micro-relief on
 * the shaft, glossy papillae mucosa on the glans/corona, deep transverse rugae
 * on the scrotum and mounds. Tapered blue-violet veins wander across the shaft
 * floor, the rails and the mound walls, lying on the exact same analytic
 * surface as the shell. Inspiration: anatomical / medical sculpture and
 * scanned-skin material studies; nothing imported.
 *
 * Contract (bench: agent/bench-extras-chibre.mjs):
 *  - each shell stays inside its collider + SIDE_MAX (0.26 m) sideways and
 *    below, and ≤ TOP_MAX above its top (standable tops only gain a few cm —
 *    feet sink into the skin, never float above it);
 *  - the shell never cuts INTO its collider (the plain box is fully hidden);
 *  - veins lie on a flat face of their host shell, ≤ TOP_MAX / SIDE_MAX out.
 * 3 draw calls (one per skin family), built once, no per-frame work.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { rng, shellGeometry, veinGeometry, veinPaths, type ShellSpec, type VeinSpec } from "../fleshShell.js";
import { fleshTextures, type FleshFamily } from "../fleshTextures.js";

export const mapIds = ["mega_chibre"];

/** the map's authored box colours (maps.ts megaChibre MC palette) → role */
const ROLE: Record<string, { fam: FleshFamily; tone: string; bulge: number; mottle: number }> = {
  "#d98a8a": { fam: "skin", tone: "#c48e7a", bulge: 1, mottle: 1 },     // shaft floor + rails
  "#c76a86": { fam: "mucosa", tone: "#c0646e", bulge: 1, mottle: 0.9 }, // glans floor + tip cap
  "#b05275": { fam: "mucosa", tone: "#a84f5f", bulge: 1, mottle: 0.8 }, // corona ridge
  "#7a2530": { fam: "mucosa", tone: "#5c1c26", bulge: 0.6, mottle: 0.5 }, // meatus notch
  "#c07f86": { fam: "rugae", tone: "#a36e60", bulge: 1, mottle: 1 },    // scrotum slab
  "#cf8890": { fam: "rugae", tone: "#b07a6b", bulge: 1, mottle: 1 },    // the mounds
  "#a86a72": { fam: "skin", tone: "#a08084", bulge: 1, mottle: 0.8 },   // vein ridges (blue under skin)
  "#c07680": { fam: "skin", tone: "#bd7f72", bulge: 1, mottle: 1 },     // veiny cover bumps
};
const VEIN_TONE = "#8f7f98";

export interface ChibreShell extends ShellSpec { fam: FleshFamily; host: number }
export interface ChibreVein extends VeinSpec { fam: FleshFamily; host: number }

const isFloor = (b: MapBox): boolean => Math.abs(b.s[1] - 1) < 0.01 && Math.abs(b.p[1] + 0.5) < 0.01;

/** Pure layout: shells + vein walks. The bench checks this against the boxes. */
export function chibreLayout(map: MapDef, lowSpec: boolean): { shells: ChibreShell[]; veins: ChibreVein[] } {
  const shells: ChibreShell[] = [];
  const veins: ChibreVein[] = [];
  const seg = lowSpec ? 1.4 : 0.6;
  // floor stacking: the redder/more specific surface wins where slabs overlap
  const floorRank: Record<string, number> = { skin: 0, rugae: 1, mucosa: 2 };
  map.boxes.forEach((b, i) => {
    const role = b.color ? ROLE[b.color.toLowerCase()] : undefined;
    if (!role) return;
    const floor = isFloor(b);
    let rank = floor ? floorRank[role.fam] : 0;
    // tip cap sits under the glans slab where they overlap
    if (floor && role.fam === "mucosa" && b.s[2] < 10) rank = 1.5;
    shells.push({
      box: b, host: i, fam: role.fam, rank, bulge: role.bulge, seed: 11 + i * 7,
      seg: Math.min(seg, Math.max(0.15, Math.min(b.s[0], b.s[1], b.s[2]) / 2)),
      color: new THREE.Color(role.tone), mottle: role.mottle,
    });
  });
  const R = rng(0xc41b);
  const add = (s: ChibreShell, axis: number, sign: 1 | -1, u: number, v: number, heading: number, length: number, radius: number, wander = 1): void => {
    veins.push({ shell: s, host: s.host, fam: s.fam, axis, sign, u, v, heading, length, radius, wander, seed: (R() * 1e9) | 0 });
  };
  for (const s of shells) {
    const b = s.box;
    const top = b.p[1] + b.s[1] / 2;
    const x0 = b.p[0] - b.s[0] / 2, z0 = b.p[2] - b.s[2] / 2, z1 = b.p[2] + b.s[2] / 2;
    const tone = b.color!.toLowerCase();
    if (tone === "#d98a8a" && isFloor(b)) {
      // the shaft floor: long dorsal veins + tributaries (thin — it is a floor)
      const n = lowSpec ? 3 : 7;
      for (let k = 0; k < n; k++) {
        const u = x0 + 2 + R() * (b.s[0] - 4);
        const v = z0 + 3 + R() * (b.s[2] - 6);
        add(s, 1, 1, u, v, (R() < 0.5 ? -1 : 1) * Math.PI / 2 + (R() - 0.5) * 0.9, 9 + R() * 12, 0.095, 1.7);
      }
    } else if (tone === "#a86a72") {
      // vein ridges: a bundle of 2-3 swollen vessels running along the top
      const along = b.s[2] > b.s[0];
      const n = lowSpec ? 1 : 3;
      for (let k = 0; k < n; k++) {
        const off = (k - (n - 1) / 2) * 0.5;
        if (along) add(s, 1, 1, b.p[0] + off, z0 + 0.3, Math.PI / 2 + (R() - 0.5) * 0.15, b.s[2] - 0.6, 0.1, 0.12);
        else add(s, 1, 1, x0 + 0.3, b.p[2] + off, (R() - 0.5) * 0.15, b.s[0] - 0.6, 0.1, 0.12);
      }
    } else if (tone === "#d98a8a") {
      // side rails: veins along both long faces, mid-height above the floor
      const n = lowSpec ? 1 : 3;
      for (const sign of [-1, 1] as const) for (let k = 0; k < n; k++) {
        add(s, 0, sign, Math.min(top - 0.3, 0.45 + R() * 0.2), z0 + 2 + R() * (b.s[2] - 4), (R() < 0.5 ? 0 : Math.PI) + Math.PI / 2 + (R() - 0.5) * 0.5, 10 + R() * 14, 0.08);
      }
    } else if (tone === "#cf8890" && b.s[0] > 15) {
      // big lower mound tier: veins crawling over each wall
      const n = lowSpec ? 1 : 2;
      for (const [axis, sign] of [[0, -1], [0, 1], [2, -1], [2, 1]] as const) for (let k = 0; k < n; k++) {
        // axis 0 → (u,v) = (y,z); axis 2 → (x,y)
        if (axis === 0) add(s, 0, sign, 0.1 + R() * 0.8, z0 + 3 + R() * (b.s[2] - 6), Math.PI / 2 + (R() - 0.5) * 0.8, 8 + R() * 8, 0.09);
        else add(s, 2, sign, x0 + 3 + R() * (b.s[0] - 6), 0.1 + R() * 0.8, (R() - 0.5) * 0.8, 8 + R() * 8, 0.09);
      }
    } else if (tone === "#c07680" && !lowSpec) {
      // veiny cover bumps: one climbing vein per long face
      for (const sign of [-1, 1] as const) add(s, 0, sign, top - 1.8, b.p[2] + (R() - 0.5), (R() - 0.5) * 0.7, 2.6, 0.08);
    } else if (tone === "#c07f86" && isFloor(b) && !lowSpec) {
      // a few faint veins on the scrotum floor around the mounds
      for (let k = 0; k < 3; k++) add(s, 1, 1, b.p[0] + (R() - 0.5) * 4, z0 + 1 + R() * 2, Math.PI / 2 + (R() - 0.5) * 0.6, 7 + R() * 6, 0.07, 1.6);
    }
  }
  return { shells, veins };
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const { shells, veins } = chibreLayout(ctx.map, ctx.lowSpec);
  if (!shells.length) return null;
  const groups = new Map<FleshFamily, THREE.BufferGeometry[]>();
  // shells and tubes are both indexed: merge them indexed (a non-indexed
  // copy would be ~6x the vertices for no visual difference)
  const push = (f: FleshFamily, g: THREE.BufferGeometry): void => {
    (groups.get(f) ?? groups.set(f, []).get(f)!).push(g);
  };
  const tile: Record<FleshFamily, number> = { skin: 1.6, mucosa: 1.4, rugae: 1.3 };
  for (const s of shells) push(s.fam, shellGeometry(s, tile[s.fam]));
  const veinCol = new THREE.Color(VEIN_TONE);
  const radial = ctx.lowSpec ? 4 : 7;
  for (const v of veins) for (const p of veinPaths(v)) push(v.fam === "mucosa" ? "skin" : v.fam, veinGeometry(p, veinCol, radial, tile.skin));

  const root = new THREE.Group();
  root.name = "extras:mega_chibre";
  for (const [fam, geos] of groups) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[mega_chibre extras] merge failed for ${fam}`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, fleshMaterial(fam, ctx.lowSpec));
    mesh.name = `mc:${fam}`;
    mesh.castShadow = ctx.shadows;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    root.add(mesh);
  }
  return root;
}

function fleshMaterial(fam: FleshFamily, lowSpec: boolean): THREE.Material {
  const t = fleshTextures(fam, lowSpec);
  const p = {
    skin: { roughness: 0.68, sheen: 0.45, clearcoat: 0.06, cr: 0.55, ns: 0.9 },
    mucosa: { roughness: 0.5, sheen: 0.25, clearcoat: 0.4, cr: 0.28, ns: 0.6 },
    rugae: { roughness: 0.78, sheen: 0.5, clearcoat: 0.0, cr: 0.6, ns: 1.2 },
  }[fam];
  const base: THREE.MeshStandardMaterialParameters = {
    color: 0xffffff, vertexColors: true, map: t.map, normalMap: t.normal, roughnessMap: t.rough,
    normalScale: new THREE.Vector2(p.ns, p.ns), roughness: p.roughness, metalness: 0,
  };
  if (lowSpec) return new THREE.MeshStandardMaterial(base);
  return new THREE.MeshPhysicalMaterial({
    ...base,
    sheen: p.sheen, sheenRoughness: 0.55, sheenColor: new THREE.Color("#ff9d8c"),
    clearcoat: p.clearcoat, clearcoatRoughness: p.cr,
  });
}
